import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { getAnalyticsMachines, getMachineTimeline } from 'features/operations/opsAnalyticsSlice'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { IOpsMachineRow } from 'types/opsTimelines.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { LOSS_BUCKET_META } from 'pages/Operations/Shared/lossClassifier'
import { LOSS_BUCKET_TO_UI } from 'pages/Operations/Shared/opsEnumMaps'
import { OpsLossBucket } from 'types/opsAnalytics.interface'
import { BucketLegend, DayStrip } from './DayStrip'
import { BarFill, BarTrack, DangerDot, ToneValue } from './Timelines.styled'

const fmtInt = (value: number): string => value.toLocaleString('en-US')

const QUICK_DAYS = ['Today', 'Yesterday']

const PICKER_MIN_DATE = new Date(new Date().getFullYear(), 5, 2)

const utilTone = (pct: number): IStatusPill['status'] =>
  pct >= 80 ? 'success' : pct >= 65 ? 'warning' : 'danger'

const apiDateForLabel = (label: string, picked: Date | null): string => {
  if (picked) return picked.toISOString().slice(0, 10)
  const d = new Date()
  if (label === 'Yesterday') d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const machineColumns: IHonestTableColumn<IOpsMachineRow>[] = [
  {
    id: 'machine',
    header: 'Machine',
    sticky: true,
    minWidth: '10rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        {item.worst && <DangerDot title='Lowest utilization this day' />}
        <BodyText weight='600'>{item.machineName}</BodyText>
      </Flex>
    ),
  },
  {
    id: 'utilization',
    header: 'Utilization',
    minWidth: '11rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        <BarTrack>
          <BarFill $pct={item.utilizationPct} $tone={utilTone(item.utilizationPct)} />
        </BarTrack>
        <ToneValue $tone={utilTone(item.utilizationPct)}>{item.utilizationPct}%</ToneValue>
      </Flex>
    ),
  },
  {
    id: 'availability',
    header: 'Availability',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.availabilityPct < 80 ? 'danger' : undefined}>
        {item.availabilityPct}%
      </ToneValue>
    ),
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.loads}</BodyText>,
  },
  {
    id: 'tonnes',
    header: 'Tonnes',
    align: 'right',
    cell: (item) => <BodyText>{fmtInt(item.tonnes)}</BodyText>,
  },
  {
    id: 'breakdowns',
    header: 'Breakdowns',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.breakdownCount > 0 ? 'danger' : undefined}>{item.breakdownCount}</ToneValue>
    ),
  },
  {
    id: 'cost',
    header: 'Cost of day',
    align: 'right',
    cell: (item) => <BodyText>{`GHS ${fmtInt(item.costOfDay)}`}</BodyText>,
  },
]

export const MachineTimelines = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { machines, machineTimeline, loaded, isLoading } = useSelector((s: RootState) => s.opsAnalytics)
  const [selectedDay, setSelectedDay] = useState('Yesterday')
  const [pickedDate, setPickedDate] = useState<Date | null>(null)

  const queryDate = apiDateForLabel(selectedDay, pickedDate)

  useEffect(() => {
    dispatch(getAnalyticsMachines({ date: queryDate }))
  }, [dispatch, queryDate])

  useEffect(() => {
    if (!id) return
    dispatch(getMachineTimeline({ id: Number(id), params: { from: queryDate, to: queryDate } }))
  }, [dispatch, id, queryDate])

  const rows: IOpsMachineRow[] = useMemo(() => {
    const sorted = [...machines].sort((a, b) => a.utilizationPct - b.utilizationPct)
    const worstPct = sorted.length ? sorted[0].utilizationPct : 0
    return sorted.map((m) => ({
      id: m.machineId,
      machineId: m.machineId,
      machineName: m.name,
      dateLabel: selectedDay,
      shiftName: 'Day Shift',
      segments: [],
      hoursByBucket: [],
      utilizationPct: Math.round(m.utilizationPct),
      availabilityPct: Math.round(m.availabilityPct),
      loads: m.loads,
      tonnes: m.tonnes,
      fuelAddedL: 0,
      smuDeltaH: 0,
      breakdownCount: m.breakdowns,
      costOfDay: m.costOfDay,
      operator: null,
      worst: m.utilizationPct === worstPct,
    }))
  }, [machines])

  const timelineSegments = useMemo(
    () =>
      (machineTimeline?.segments ?? []).map((seg, index) => ({
        id: index + 1,
        fromMin: 0,
        toMin: 60,
        bucket: (LOSS_BUCKET_TO_UI[seg.bucket] ?? 'NO_TASK') as OpsLossBucket,
        activity: null,
        taskName: null,
        taskId: null,
        location: `${seg.from} – ${seg.to}`,
        note: null,
      })),
    [machineTimeline],
  )

  if (id) {
    const name = machines.find((m) => m.machineId === Number(id))?.name ?? `Machine ${id}`
    return (
      <>
        <NavBar />
        <PageWrapper>
          <H1WithBack title={name} arrow onClick={() => navigate('/operations/analytics/machines')} />
          <Flex mb='16'>
            <OtherText grey>{selectedDay} · timeline from shift aggregates</OtherText>
          </Flex>
          {loaded && !isLoading && !timelineSegments.length ? (
            <NoData text='No timeline recorded for this machine on the selected day.' />
          ) : (
            <Container>
              <ContainerHeader>
                <TitleText>{selectedDay}</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                {timelineSegments.length > 0 && (
                  <DayStrip
                    segments={timelineSegments}
                    dateLabel={name}
                    startHour={6}
                    windowHours={24}
                  />
                )}
                <BucketLegend />
              </ContainerContent>
            </Container>
          )}
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Head1>Machine timelines</Head1>
        <Flex mb='16'>
          <OtherText grey>{`${selectedDay} · fleet day summary`}</OtherText>
        </Flex>
        <Flex mb='16' center gap='0.75rem' wrap>
          <TabsContainer>
            {QUICK_DAYS.map((label) => (
              <Tabs key={label} active={selectedDay === label} onClick={() => { setSelectedDay(label); setPickedDate(null) }}>
                {label}
              </Tabs>
            ))}
          </TabsContainer>
          <CalendarInput
            name='timelineDay'
            placeholder='Jump to date'
            width='12rem'
            nomarg
            defaultValue={pickedDate}
            minDate={PICKER_MIN_DATE}
            maxDate={new Date()}
            onChange={(date) => {
              setPickedDate(date)
              if (date) setSelectedDay(date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }))
            }}
          />
        </Flex>
        {loaded && !isLoading && !rows.length ? (
          <NoData text='Timelines are computed at shift close once connected.' />
        ) : (
          <HonestTable
            columns={machineColumns}
            data={rows}
            keyField='id'
            emptyMessage='No records for this day.'
            onClick={(item) => navigate(`/operations/analytics/machines/${item.machineId}`)}
          />
        )}
      </PageWrapper>
    </>
  )
}
